/**
 * Ring Curator - Product Filtering System
 *
 * Payload rules (INTERNAL):
 * - If a group has selections -> payload uses ONLY selected values (comma-separated)
 * - If a group has NO selections -> payload uses ALL UI values for that group (comma-separated)
 *
 * UI rules:
 * - NEVER auto-select options for the user
 * - Only user clicks create selections
 *
 * URL rules (FIXED):
 * - Do NOT write query params on initial page load when URL has no params
 * - URL should contain ONLY what user explicitly selected
 *   (example: ?style=trilogy)
 * - Do NOT put "all UI values" into URL ever (that's internal-only)
 * - Stock toggle may write stock=1 when enabled
 *
 * Optional exclusions supported via dataset:
 * - data-exclude-handles="handle-1,handle-2"
 * - data-exclude-vendors="vendor a,vendor b"
 * - data-exclude-title-contains="lab grown,placeholder"
 */

(function () {
  "use strict";

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initRingCurator);
  } else {
    initRingCurator();
  }

  function initRingCurator() {
    document
      .querySelectorAll('[data-section-id^="ring-curator"]')
      .forEach((section) => new RingCurator(section));
  }

  class RingCurator {
    constructor(rootElement) {
      this.root = rootElement;
      this.sectionId = this.root.dataset.sectionId;

      this.initConfig();
      this.initElements();
      this.initState();
      this.init();
    }

    initConfig() {
      const ds = this.root.dataset;

      const parseCsv = (v) =>
        (v || "")
          .split(",")
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean);

      this.config = {
        storefrontToken: ds.storefrontToken,
        collectionHandle: ds.collectionHandle,
        metafieldNamespace: ds.metafieldNamespace,
        styleKey: ds.styleKey,
        cutKey: ds.cutKey,
        stoneKey: ds.stoneKey,
        metalKey: ds.metalKey,
        perPage: parseInt(ds.perPage) || 12,
        showVendor: ds.showVendor === "true",
        resultHeadingTemplate:
          ds.resultHeadingTemplate || "{count} curated rings found just for you!",

        excludeHandles: new Set(parseCsv(ds.excludeHandles)),
        excludeVendors: new Set(parseCsv(ds.excludeVendors)),
        excludeTitleContains: parseCsv(ds.excludeTitleContains)
      };
    }

    initElements() {
      const suffix = this.sectionId.replace("ring-curator-", "");
      this.elements = {
        results: document.getElementById(`ring-curator-results-${suffix}`),
        grid: document.getElementById(`ring-curator-grid-${suffix}`),
        empty: document.getElementById(`ring-curator-empty-${suffix}`),
        pagination: document.getElementById(`ring-curator-pagination-${suffix}`),
        filterItems: this.root.querySelectorAll("[data-filter-group][data-filter-value]"),
        resultHeading: this.root.querySelector(".ring-curator__result-heading"),
        clearButton: this.root.querySelector("[data-clear-curator]"),
        resultsHeader: this.root.querySelector("[data-results-header]"),
        noActiveFilterText: this.root.querySelector(".no-active-filter"),
        stockToggle: this.root.querySelector("[data-stock-toggle]"),
        stockToggleWrapper: this.root.querySelector("[data-stock-toggle-wrapper]")
      };
    }

    initState() {
      this.state = {
        allProducts: [],
        filteredProducts: [],
        visibleCount: this.config.perPage,
        isLoaded: false,
        isLoading: false,
        isInitializing: false,
        inStockOnly: false,

        // URL / interaction control
        hasUserInteracted: false,

        // ONLY user selections live here
        selected: {
          style: new Set(),
          cut: new Set(),
          stone: new Set(),
          metal: new Set()
        },

        // UI options that exist on page (section blocks)
        uiOptions: {
          style: new Set(),
          cut: new Set(),
          stone: new Set(),
          metal: new Set()
        }
      };
    }

    init() {
      // 1) Collect UI options
      this.collectUiOptions();

      this.bindFilters();
      this.bindClearButton();
      this.bindStockToggle();

      // 2) Load products first
      this.loadProducts().then(() => {
        console.log("✅ Ring Curator ready!");

        // ✅ If URL has params, apply them AFTER products + UI options exist
        if (this.hasMeaningfulUrlParams()) {
          // force apply, but do NOT click items (avoid scroll / double updates)
          this.applyUrlFilters(true);
          this.updateUIAfterFilterChange(true);
        } else {
          // Normal behaviour: no URL params means no results shown until click
          this.updateUIAfterFilterChange(true);
        }
      });
    }

    hasAnyQueryParams() {
      return !!(window.location.search && window.location.search.length > 1);
    }

    collectUiOptions() {
      console.log("🎨 Collecting UI filter options...");

      this.elements.filterItems.forEach((item) => {
        const group = item.getAttribute("data-filter-group");
        const value = item.getAttribute("data-filter-value")?.trim().toLowerCase();
        if (group && value && this.state.uiOptions[group]) {
          this.state.uiOptions[group].add(value);
        }
      });

      console.log("\n=== 🖥️ UI FILTERS AVAILABLE ===");
      console.log("  Styles:", Array.from(this.state.uiOptions.style).join(", ") || "❌ None");
      console.log("  Cuts:", Array.from(this.state.uiOptions.cut).join(", ") || "❌ None");
      console.log("  Stones:", Array.from(this.state.uiOptions.stone).join(", ") || "❌ None");
      console.log("  Metals:", Array.from(this.state.uiOptions.metal).join(", ") || "❌ None");
      console.log("================================\n");
    }

    scrollToSection(selector) {
      const target = document.querySelector(selector);
      if (!target) return;
      const OFFSET = 100;
      const y = target.getBoundingClientRect().top + window.pageYOffset - OFFSET;
      window.scrollTo({ top: y, behavior: "smooth" });
    }

    // ==========================
    // PAYLOAD (internal: selected OR all UI values)
    // ==========================
    getPayloadValues(group) {
      const selected = Array.from(this.state.selected[group] || []);
      if (selected.length) return selected;
      return Array.from(this.state.uiOptions[group] || []);
    }

    getPayloadCsv(group) {
      return this.getPayloadValues(group).join(",");
    }

    getPayloadObject() {
      return {
        style: this.getPayloadCsv("style"),
        cut: this.getPayloadCsv("cut"),
        stone: this.getPayloadCsv("stone"),
        metal: this.getPayloadCsv("metal"),
        inStockOnly: this.state.inStockOnly
      };
    }

    // ==========================
    // URL (external: ONLY user selections)
    // ==========================
    getSelectedCsv(group) {
      const selected = Array.from(this.state.selected[group] || []);
      return selected.length ? selected.join(",") : "";
    }

    getUrlObject() {
      return {
        style: this.getSelectedCsv("style"),
        cut: this.getSelectedCsv("cut"),
        stone: this.getSelectedCsv("stone"),
        metal: this.getSelectedCsv("metal"),
        inStockOnly: this.state.inStockOnly
      };
    }

    logPayload(action = "") {
      const payload = this.getPayloadObject();
      console.log("\n📤 EFFECTIVE PAYLOAD (internal: selected OR all UI values):");
      if (action) console.log(`📌 ${action}`);
      console.table(payload);

      const urlObj = this.getUrlObject();
      console.log("🔗 URL VALUES (external: selected only):");
      console.table({
        style: urlObj.style || "—",
        cut: urlObj.cut || "—",
        stone: urlObj.stone || "—",
        metal: urlObj.metal || "—",
        stock: urlObj.inStockOnly ? "1" : "—"
      });

      return payload;
    }

    // === URL MANAGEMENT ===
    getUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const parseParam = (key) =>
        params.get(key)?.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean) || [];
      return {
        style: parseParam("style"),
        cut: parseParam("cut"),
        stone: parseParam("stone"),
        metal: parseParam("metal"),
        stock: params.get("stock") === "1"
      };
    }

    /**
     * Update URL based on SELECTED ONLY.
     *
     * RULE:
     * - If URL has NO params and user has NOT interacted, do nothing.
     * - If URL already had params OR user interacted, sync it.
     * - Only write params for groups where user selected values.
     */
    updateUrlParams(force = false) {
      if (this.state.isInitializing && !force) return;

      if (!force && !this.state.hasUserInteracted && !this.hasAnyQueryParams()) {
        return;
      }

      const urlObj = this.getUrlObject();
      const params = new URLSearchParams(window.location.search);

      ["style", "cut", "stone", "metal"].forEach((group) => {
        const csv = urlObj[group];
        if (csv) params.set(group, csv);
        else params.delete(group);
      });

      urlObj.inStockOnly ? params.set("stock", "1") : params.delete("stock");

      const qs = params.toString();
      const newUrl = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;

      window.history.replaceState({}, "", newUrl);
      console.log("🔗 URL updated (selected only):", newUrl);
      this.logPayload("URL Updated");
    }

    /**
     * Apply URL filters to selections WITHOUT clicking.
     * - supports force apply
     * - if URL includes values equal to ALL UI values for a group, treat as no selection (do NOT auto-select all)
     */
    applyUrlFilters(force = false) {
      const urlParams = this.getUrlParams();

      const hasAny =
        urlParams.style.length ||
        urlParams.cut.length ||
        urlParams.stone.length ||
        urlParams.metal.length ||
        urlParams.stock;

      if (!hasAny && !force) return;

      // Apply stock from URL
      if (urlParams.stock) {
        this.state.inStockOnly = true;
        if (this.elements.stockToggle) {
          this.elements.stockToggle.setAttribute("aria-pressed", "true");
          this.elements.stockToggle.classList.add("is-on");
        }
      }

      const isAllForGroup = (group, values) => {
        const ui = Array.from(this.state.uiOptions[group] || []);
        if (!ui.length) return false;

        const uiSet = new Set(ui.map((v) => v.toLowerCase()));
        const valSet = new Set((values || []).map((v) => v.toLowerCase()));

        if (uiSet.size !== valSet.size) return false;
        for (const v of uiSet) if (!valSet.has(v)) return false;
        return true;
      };

      this.state.isInitializing = true;

      // 1) Clear any existing UI active classes + sets (only for URL application)
      // NOTE: This is "initialization only" usage, not user action.
      ["style", "cut", "stone", "metal"].forEach((group) => this.state.selected[group].clear());
      this.elements.filterItems.forEach((item) => item.classList.remove("active"));

      // 2) Apply subset selections from URL into sets + UI classes (NO clicks)
      this.elements.filterItems.forEach((item) => {
        const group = item.getAttribute("data-filter-group");
        const value = item.getAttribute("data-filter-value")?.trim().toLowerCase();
        if (!group || !value) return;

        const vals = urlParams[group] || [];
        if (!vals.length) return;

        // If URL equals all -> do not auto-select
        if (isAllForGroup(group, vals)) return;

        if (vals.includes(value)) {
          this.state.selected[group].add(value);
          item.classList.add("active");
        }
      });

      setTimeout(() => {
        this.state.isInitializing = false;
        // Normalize URL to "selected-only" (removes anything not explicitly selected)
        this.updateUrlParams(true);
      }, 0);
    }

    // === API ===
    getEndpoint() {
      return (window.Shopify?.routes?.root || "") + "api/2024-01/graphql.json";
    }

    getGraphQLQuery() {
      const { metafieldNamespace: ns, styleKey, cutKey, stoneKey, metalKey } = this.config;

      return `
        query getCuratedRings($handle: String!, $first: Int!) {
          collection(handle: $handle) {
            products(first: $first) {
              edges {
                node {
                  id
                  title
                  handle
                  vendor
                  description
                  availableForSale
                  featuredImage { url altText }
                  images(first: 4) { edges { node { url altText } } }
                  priceRange { minVariantPrice { amount currencyCode } }
                  compareAtPriceRange { minVariantPrice { amount currencyCode } }
                  variants(first: 1) { edges { node { id } } }
                  styleMetafield: metafield(namespace: "${ns}", key: "${styleKey}") { value }
                  cutMetafield: metafield(namespace: "${ns}", key: "${cutKey}") { value }
                  stoneMetafield: metafield(namespace: "${ns}", key: "${stoneKey}") { value }
                  metalMetafield: metafield(namespace: "${ns}", key: "${metalKey}") { value }
                  brandMetafield: metafield(namespace: "custom", key: "h2_brand_name") { value }
                }
              }
            }
          }
        }
      `;
    }

    hasMeaningfulUrlParams() {
      const params = this.getUrlParams();
      return (
        params.style.length ||
        params.cut.length ||
        params.stone.length ||
        params.metal.length ||
        params.stock
      );
    }

    getEffectiveGroupValues(group) {
      const selected = Array.from(this.state.selected[group] || []);
      if (selected.length > 0) return selected;
      return Array.from(this.state.uiOptions[group] || []);
    }

    async loadProducts() {
      if (this.state.isLoaded || this.state.isLoading) return;
      if (!this.config.storefrontToken || !this.config.collectionHandle) return;

      this.state.isLoading = true;
      console.log("🔄 Loading products...");

      try {
        const response = await fetch(this.getEndpoint(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": this.config.storefrontToken
          },
          body: JSON.stringify({
            query: this.getGraphQLQuery(),
            variables: { handle: this.config.collectionHandle, first: 250 }
          })
        });

        const data = await response.json();
        const collection = data.data?.collection;
        if (!collection) throw new Error("No collection found");

        const products = collection.products?.edges || [];

        const mapped = products.map(({ node }) => ({
          ...node,
          _curator: {
            style: node.styleMetafield?.value,
            cut: node.cutMetafield?.value,
            stone: node.stoneMetafield?.value,
            metal: node.metalMetafield?.value,
            brand: node.brandMetafield?.value
          }
        }));

        this.state.allProducts = mapped.filter((p) => {
          const handle = (p.handle || "").toLowerCase();
          const vendor = (p.vendor || "").toLowerCase();
          const title = (p.title || "").toLowerCase();

          if (this.config.excludeHandles?.has(handle)) return false;
          if (this.config.excludeVendors?.has(vendor)) return false;

          if (Array.isArray(this.config.excludeTitleContains) && this.config.excludeTitleContains.length) {
            if (this.config.excludeTitleContains.some((needle) => needle && title.includes(needle))) {
              return false;
            }
          }
          return true;
        });

        console.log(`✅ Loaded ${this.state.allProducts.length} products (after exclusions)`);
        this.state.isLoaded = true;
      } catch (err) {
        console.error("❌ Error loading products:", err);
      } finally {
        this.state.isLoading = false;
      }
    }

    // === NORMALIZE VALUES ===
    normalizeValues(raw) {
      return raw
        ? String(raw)
            .split(",")
            .map((v) => v.trim().toLowerCase())
            .filter(Boolean)
        : [];
    }

    normalizeMetal(raw) {
      if (!raw) return [];
      const value = raw.toLowerCase();
      if (value.includes("yellow gold")) return ["yellow gold"];
      if (value.includes("white gold")) return ["white gold"];
      if (value.includes("rose gold")) return ["rose gold"];
      if (value.includes("platinum")) return ["platinum"];
      if (value.includes("18ct yellow gold")) return ["18ct yellow gold"];
      if (value.includes("18ct white gold")) return ["18ct white gold"];
      return [value];
    }

    anySelected() {
      return ["style", "cut", "stone", "metal"].some((g) => this.state.selected[g].size > 0);
    }

    productMatches(product) {
      const groupMatches = (group) => {
        // ✅ Use payload values (selected OR all UI values)
        const effective = this.getPayloadValues(group);

        // If there are truly no options in UI for this group, don't restrict
        if (!effective || !effective.length) return true;

        const productVals =
          group === "metal"
            ? this.normalizeMetal(product._curator[group])
            : this.normalizeValues(product._curator[group]);

        if (!productVals.length) return false;

        // ✅ OR within group (robust matching for messy metafields)
        return effective.some((selectedVal) =>
          productVals.some(
            (productVal) =>
              productVal.includes(selectedVal) || selectedVal.includes(productVal)
          )
        );
      };

      // ✅ AND across groups
      const facetsMatch = ["style", "cut", "stone", "metal"].every((group) => groupMatches(group));

      const stockMatch = !this.state.inStockOnly || product.availableForSale !== false;
      return facetsMatch && stockMatch;
    }

    logEffectiveFilters(context = "") {
      console.log("\n🧪 EFFECTIVE FILTER SET");
      if (context) console.log(`Context: ${context}`);

      ["style", "cut", "stone", "metal"].forEach((group) => {
        console.log(`${group}:`, this.getEffectiveGroupValues(group).join(", ") || "❌ none");
      });

      console.log("In stock only:", this.state.inStockOnly);
      console.log("================================\n");
    }

    // === LOG ===
    logFilterStatus(action = "") {
      console.log("\n=== CURRENT FILTER STATUS ===");
      if (action) console.log(`📌 ${action}`);
      console.log(
        `  🎨 Style (selected):   ${Array.from(this.state.selected.style).join(", ") || "❌ None selected"}`
      );
      console.log(
        `  ✂️ Cut (selected):     ${Array.from(this.state.selected.cut).join(", ") || "❌ None selected"}`
      );
      console.log(
        `  💎 Stone (selected):   ${Array.from(this.state.selected.stone).join(", ") || "❌ None selected"}`
      );
      console.log(
        `  🔩 Metal (selected):   ${Array.from(this.state.selected.metal).join(", ") || "❌ None selected"}`
      );
      console.log(`  📦 In Stock: ${this.state.inStockOnly ? "✅ ON" : "⚪ OFF"}`);
      console.log("================================\n");

      this.logPayload(action || "Status");
    }

    bindStockToggle() {
      if (!this.elements.stockToggle) return;

      this.elements.stockToggle.addEventListener("click", (e) => {
        e.preventDefault();
        this.state.hasUserInteracted = true;

        this.state.inStockOnly = !this.state.inStockOnly;
        this.elements.stockToggle.setAttribute("aria-pressed", this.state.inStockOnly ? "true" : "false");
        this.elements.stockToggle.classList.toggle("is-on", this.state.inStockOnly);

        this.logFilterStatus(`Stock Toggle: ${this.state.inStockOnly ? "ON" : "OFF"}`);
        this.updateUIAfterFilterChange();
      });
    }

    applyFilters() {
      this.logEffectiveFilters("applyFilters()");
      if (!this.anySelected()) {
        this.state.filteredProducts = [];
        return;
      }

      const payload = this.getPayloadObject();

      console.log("\n🎯 APPLYING FILTERS (payload values):");
      console.log(`  Style: ${payload.style || "❌ no ui options"}`);
      console.log(`  Cut: ${payload.cut || "❌ no ui options"}`);
      console.log(`  Stone: ${payload.stone || "❌ no ui options"}`);
      console.log(`  Metal: ${payload.metal || "❌ no ui options"}`);
      console.log(`  In Stock Only: ${payload.inStockOnly ? "YES" : "NO"}`);
      console.log("-----------------------------------");

      const products = this.state.allProducts.filter((p) => this.productMatches(p));
      console.log(`  📦 Products found: ${products.length}`);

      products.sort((a, b) => {
        const aAvail = a.availableForSale !== false;
        const bAvail = b.availableForSale !== false;
        return aAvail === bAvail ? 0 : aAvail ? -1 : 1;
      });

      this.state.filteredProducts = products;
    }

    // === UI RENDERING ===
    formatMoney(amount, currencyCode = "GBP") {
      if (amount == null || isNaN(amount)) return "";
      return new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    }

    refreshWishlistHeroButtons() {
      try {
        const buttons = this.root.querySelectorAll(".wishlist-hero-custom-button");
        buttons.forEach((btn) => {
          const ev = new CustomEvent("wishlist-hero-add-to-custom-element", { detail: btn });
          document.dispatchEvent(ev);
        });
      } catch (e) {
        console.warn("Wishlist Hero refresh failed", e);
      }
    }

    renderLoadMore() {
      if (!this.elements.pagination) return;
      this.elements.pagination.innerHTML = "";

      const total = this.state.filteredProducts.length;
      if (total <= this.state.visibleCount) {
        this.elements.pagination.style.display = "none";
        return;
      }

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "button button--primary load-more-btn";
      btn.innerHTML = `
        <span>Load More</span>
        <div class="loading-overlay__spinner hidden">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>
      `;

      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.classList.add("loading");
        btn.querySelector("span").textContent = "Loading...";
        btn.querySelector(".loading-overlay__spinner").classList.remove("hidden");

        setTimeout(() => {
          this.state.visibleCount += this.config.perPage;
          this.renderGrid();
        }, 300);
      });

      this.elements.pagination.appendChild(btn);
      this.elements.pagination.style.display = "flex";
    }

    createProductCard(product) {
      const priceObj = product.priceRange?.minVariantPrice;
      const compareObj = product.compareAtPriceRange?.minVariantPrice;
      const price = priceObj ? parseFloat(priceObj.amount) : null;
      const compareAt = compareObj ? parseFloat(compareObj.amount) : null;
      const currency = priceObj?.currencyCode || compareObj?.currencyCode || "GBP";
      const hasCompare = compareAt != null && price != null && compareAt > price;
      const isAvailable = product.availableForSale !== false;
      const productUrl = `/products/${product.handle}`;
      const variantId = product.variants?.edges?.[0]?.node?.id || "";
      const variantUrl = variantId ? `?variant=${variantId}` : "";

      const imageEdges = product.images?.edges || [];
      const mainImage =
        product.featuredImage?.url ||
        imageEdges[0]?.node?.url ||
        "https://cdn.shopify.com/s/images/themes/product-1.png";
      
      
      const mainAlt = product.featuredImage?.altText || imageEdges[0]?.node?.altText || product.title;
      const hoverImage = imageEdges.length > 1 ? imageEdges[1].node.url : null;
      const hoverAlt = imageEdges.length > 1 ? imageEdges[1].node.altText || product.title : mainAlt;

      const formattedPrice = this.formatMoney(price, currency);
      const formattedComparePrice = this.formatMoney(compareAt, currency);

      return `
        <div class="spf-col-xl-3 spf-col-lg-3 spf-col-md-6 spf-col-sm-6 spf-col-6 product-grid__item">
          <div class="spf-product-card spf-product-card__left collection-inner-card spf-product-card__template-1${!isAvailable ? " spf-soldout" : ""}">
            <div class="spf-product-card__inner ring-curator-card-inner">
              
              ${
                !isAvailable
                  ? `<span class="spf-product__label spf-product__label-soldout">Sold out</span>`
                  : hasCompare
                  ? `<span class="spf-product__label spf-product__label-sale sale-text">Sale</span>`
                  : ""
              }
              <a class="spf-product-card__image-wrapper ring-curator-image-wrapper" href="${productUrl}${variantUrl}" style="padding-top:100%;">
                <img class="spf-product-card__image spf-product-card__image-main ring-curator-image-primary" src="${mainImage}&width=600" alt="${mainAlt}" loading="lazy" />
                ${
                  hoverImage
                    ? `<img class="spf-product-card__image ring-curator-image-secondary" src="${hoverImage}&width=600" alt="${hoverAlt}" loading="lazy" />`
                    : ""
                }
              </a>
            </div>
            <div class="spf-product__info">
              ${
                this.config.showVendor && product._curator?.brand
                  ? `<div class="spf-product-card__vendor">${product._curator.brand}</div>`
                  : ""
              }
              <div class="h4 spf-product-card__title"><a href="${productUrl}">${product.title}</a></div>
              <div class="spf-product-card__price-wrapper">
                ${
                  hasCompare
                    ? `<span class="spf-product-card__oldprice money">${formattedComparePrice}</span><span class="spf-product-card__saleprice money">${formattedPrice}</span>`
                    : `<span class="spf-product-card__price money">${formattedPrice}</span>`
                }
              </div>
            </div>
          </div>
        </div>
      `;
    }

    renderGrid() {
      if (!this.elements.grid) return;
      this.elements.grid.innerHTML = "";

      if (!this.state.filteredProducts.length) {
        if (this.elements.resultsHeader) this.elements.resultsHeader.style.display = "none";
        if (this.elements.empty) this.elements.empty.style.display = "block";
        if (this.elements.pagination) this.elements.pagination.style.display = "none";
        return;
      }

      if (this.elements.resultsHeader) this.elements.resultsHeader.style.display = "block";
      if (this.elements.empty) this.elements.empty.style.display = "none";

      const pageItems = this.state.filteredProducts.slice(0, this.state.visibleCount);
      pageItems.forEach((product) => {
        this.elements.grid.insertAdjacentHTML("beforeend", this.createProductCard(product));
      });

      this.renderLoadMore();
      this.refreshWishlistHeroButtons();
    }

    updateResultHeading() {
      if (!this.elements.resultHeading) return;
      const count = this.state.filteredProducts.length;
      if (count === 0) {
        this.elements.resultHeading.textContent = "";
        return;
      }
      const template = this.config.resultHeadingTemplate;
      const text =
        count === 1
          ? template.replace("{count}", "1").replace("rings", "ring")
          : template.replace("{count}", count);
      this.elements.resultHeading.textContent = text;
    }

    updateUIAfterFilterChange(force = false) {
      this.logFilterStatus("UI Updated");

      // URL writes ONLY selected values
      this.updateUrlParams();

      const urlDriven = this.hasMeaningfulUrlParams();
      const shouldShowResults = this.anySelected() || urlDriven || force;

      if (!shouldShowResults) {
        if (this.elements.results) this.elements.results.style.display = "none";
        if (this.elements.noActiveFilterText) this.elements.noActiveFilterText.style.display = "block";
        if (this.elements.resultsHeader) this.elements.resultsHeader.style.display = "none";
        if (this.elements.empty) this.elements.empty.style.display = "none";
        if (this.elements.pagination) this.elements.pagination.style.display = "none";
        return;
      }

      if (this.elements.noActiveFilterText) this.elements.noActiveFilterText.style.display = "none";
      if (this.elements.results) this.elements.results.style.display = "block";

      this.state.visibleCount = this.config.perPage;
      this.applyFilters();
      this.updateResultHeading();
      this.renderGrid();
    }

    handleFilterClick(item) {
      const group = item.getAttribute("data-filter-group");
      const value = item.getAttribute("data-filter-value")?.trim().toLowerCase();
      if (!group || !value || !this.state.selected[group]) return;

      this.state.hasUserInteracted = true;
      this.logEffectiveFilters(`user clicked ${group}: ${value}`);

      const isActive = item.classList.contains("active");

      if (isActive) {
        this.state.selected[group].delete(value);
        item.classList.remove("active");
        this.logFilterStatus(`REMOVED ${group}: ${value}`);
        this.updateUIAfterFilterChange();
      } else {
        this.state.selected[group].add(value);
        item.classList.add("active");
        this.logFilterStatus(`SELECTED ${group}: ${value}`);
        this.updateUIAfterFilterChange();
      }
    }

    bindFilters() {
      this.elements.filterItems.forEach((item) => {
        item.addEventListener("click", (e) => {
          e.preventDefault();
          this.handleFilterClick(item);

          if (item.classList.contains("ring-curator-grid-style-item")) {
            requestAnimationFrame(() => this.scrollToSection(".ring-curator-section-cut"));
          }
          if (item.classList.contains("ring-curator-grid-cut-item")) {
            requestAnimationFrame(() => this.scrollToSection(".ring-curator-section-stone"));
          }
          if (item.classList.contains("ring-curator-grid-stone-item")) {
            requestAnimationFrame(() => this.scrollToSection(".ring-curator-section-metal"));
          }
          if (item.classList.contains("ring-curator-grid-metal-item")) {
            requestAnimationFrame(() => this.scrollToSection(".ring-curator-results"));
          }
        });
      });
    }

    bindClearButton() {
      if (!this.elements.clearButton) return;
      this.elements.clearButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.clearAllFilters();
      });
    }

    clearAllFilters() {
      console.log("\n🧹 CLEARING ALL FILTERS");
      this.state.hasUserInteracted = true;

      this.state.inStockOnly = false;
      if (this.elements.stockToggle) {
        this.elements.stockToggle.setAttribute("aria-pressed", "false");
        this.elements.stockToggle.classList.remove("is-on");
      }

      ["style", "cut", "stone", "metal"].forEach((group) => this.state.selected[group].clear());
      this.elements.filterItems.forEach((item) => item.classList.remove("active"));

      this.logFilterStatus("ALL FILTERS CLEARED");

      // After clear, URL should be clean
      this.updateUrlParams(true);

      this.updateUIAfterFilterChange();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }
})();